import { useMemo } from 'react';
import type { AttackEvent, CredentialPair, Stats } from '@repo/shared';
import { flagEmoji, truncate } from '../lib/format';
import styles from './HallOfShame.module.css';

const MAX_CARDS = 12;

interface CredCard extends CredentialPair {
  key: string;
  /** The most recent buffered event with this pair (for flag / ip / path), if any. */
  latest: AttackEvent | null;
}

/**
 * A curated feed of the most watchable attempts: real credential guesses bots
 * threw at the fake login forms. It's the funniest part of the show — `admin` /
 * `admin`, `root` / `123456`, and worse.
 *
 * Pairs and their `×N` counts come from the server's ring-buffer leaderboard,
 * so a reload no longer resets them to whatever one tab happened to see. The
 * client event buffer only decorates each card with its freshest sighting
 * (flag, truncated IP, path) when one is still in view.
 */
export function HallOfShame({ events, stats }: { events: AttackEvent[]; stats: Stats }) {
  const cards = useMemo<CredCard[]>(
    () =>
      stats.topCredentials.slice(0, MAX_CARDS).map((pair) => ({
        ...pair,
        key: `${pair.username ?? ''}:${pair.password ?? ''}`,
        // `events` is newest-first, so the first match is the latest sighting.
        latest:
          events.find((e) => e.username === pair.username && e.password === pair.password) ?? null,
      })),
    [events, stats.topCredentials],
  );

  if (cards.length === 0) {
    return (
      <div className={styles.wrap}>
        <h3 className={styles.title}>hall of shame</h3>
        <p className={styles.empty}>
          No credential guesses captured yet. The bots will find the login forms soon enough.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.wrap}>
      <h3 className={styles.title}>hall of shame — credentials they tried</h3>
      <div className={styles.grid}>
        {cards.map((c) => (
          <div key={c.key} className={styles.card}>
            <div className={styles.creds}>
              <span className={styles.user}>{truncate(c.username ?? '∅', 24)}</span>
              <span className={styles.slash}>/</span>
              <span className={styles.pass}>{truncate(c.password ?? '∅', 24)}</span>
              {c.count > 1 && <span className={styles.count}>×{c.count}</span>}
            </div>
            {c.latest && (
              <div className={styles.meta}>
                <span>{flagEmoji(c.latest.country)}</span>
                <span className={styles.dim}>{c.latest.ipDisplay}</span>
                <span className={styles.path}>{truncate(c.latest.path, 22)}</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
